/*
 * The Delivery Operations screen's state.
 *
 * Two layers decide whether LINE delivery actually happens: a deployment flag set at deploy time,
 * and a runtime switch on top of it. The original defect this panel was built for was that an
 * operator who turned on the deployment env var had no way to see or flip the second, so LINE
 * stayed off for every event with no visible reason.
 *
 * `effective` is the server's own answer to "is it on", and `lineBlockedBy` is why not. Both are
 * passed through untouched: the one thing this screen must never do is compute its own opinion of
 * whether delivery is live and disagree with the worker that actually sends.
 */
import { useCallback, useEffect, useState } from 'react';
import { ApiError, superadminRolloutApi } from '../../../api';
import type { DeliveryRolloutStatusDto, LineRolloutMode } from '../../../api';

export interface DeliveryRolloutState {
  status: DeliveryRolloutStatusDto | null;
  loading: boolean;
  loadError: string;
  error: string;
  busy: boolean;
  justSaved: boolean;
  setMode: (mode: LineRolloutMode) => Promise<void>;
  setMaster: (on: boolean) => Promise<void>;
  setSelectionV2: (on: boolean) => Promise<void>;
  setPortal: (on: boolean) => Promise<void>;
  setCanaryEvents: (ids: string[]) => Promise<void>;
  setEventOverride: (eventId: string, enabled: boolean | null) => Promise<void>;
  reload: () => Promise<void>;
}

export function useDeliveryRollout(token: string): DeliveryRolloutState {
  const [status, setStatus] = useState<DeliveryRolloutStatusDto | null>(null);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [justSaved, setJustSaved] = useState(false);

  const load = useCallback(async () => {
    try {
      setStatus(await superadminRolloutApi.get(token));
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดสถานะ rollout ไม่สำเร็จ');
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const run = useCallback(async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    setJustSaved(false);
    try {
      await fn();
      // Reload rather than patch in place: `effective` and `lineBlockedBy` are derived by the
      // server from both layers, and a locally-patched runtime flag would show a state the worker
      // does not agree with.
      await load();
      setJustSaved(true);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : 'บันทึกไม่สำเร็จ');
    } finally {
      setBusy(false);
    }
  }, [load]);

  return {
    status,
    loading: status === null && !loadError,
    loadError, error, busy, justSaved,
    setMode: (lineRolloutMode) => run(() => superadminRolloutApi.patch(token, { lineRolloutMode })),
    setMaster: (lineDeliveryEnabled) => run(() => superadminRolloutApi.patch(token, { lineDeliveryEnabled })),
    setSelectionV2: (deliverySelectionV2Enabled) => run(() => superadminRolloutApi.patch(token, { deliverySelectionV2Enabled })),
    setPortal: (myTicketPortalEnabled) => run(() => superadminRolloutApi.patch(token, { myTicketPortalEnabled })),
    setCanaryEvents: (lineCanaryEventIds) => run(() => superadminRolloutApi.patch(token, { lineCanaryEventIds })),
    setEventOverride: (eventId, enabled) => run(() => superadminRolloutApi.setEventLineOverride(token, eventId, enabled)),
    reload: load,
  };
}
